import {
  Middleware,
  MiddlewareAPI,
  Dispatch,
  UnknownAction
} from '@reduxjs/toolkit'
import projectDataSlice from 'store/features/projectData/slice'
import isEqual from 'lodash.isequal'
import {
  ACTION_TYPES,
  ACTION_TYPES_UPDATING_UNDO_REDO_AUDIO_ITEMS,
  ACTION_TYPES_UPDATING_UNDO_REDO_MEDIA_ITEMS
} from './consts'

import { IProjectData } from 'types/projectData'
import { TRootState } from 'store/types'
import { MediaStatus, TPartialMediaItem } from 'types/media'

// Types for better type safety
interface IActionWithUndoConfig {
  type: string
  payload: {
    config?: {
      isUndoable?: boolean
    }
  }
  savedStateOfProjectData?: IProjectData
}

// Helper functions for better readability
const isUndoableAction = (action: unknown): action is IActionWithUndoConfig => {
  const actionType = (action as { type: string }).type
  const isUndoable =
    (action as IActionWithUndoConfig).payload?.config?.isUndoable ?? true

  return ACTION_TYPES.includes(actionType) && isUndoable
}

const isUpdatingUndoRedoAudioItems = (action: unknown): boolean => {
  const actionType = (action as { type: string }).type
  return ACTION_TYPES_UPDATING_UNDO_REDO_AUDIO_ITEMS.includes(actionType)
}

export const isUpdatingUndoRedoMediaItems = (action: unknown): boolean => {
  const actionType = (action as { type: string }).type
  return ACTION_TYPES_UPDATING_UNDO_REDO_MEDIA_ITEMS.includes(actionType)
}

const getCurrentProjectData = (
  store: MiddlewareAPI<Dispatch<UnknownAction>, TRootState>
): IProjectData => {
  const currentState = store.getState()
  return currentState.projectData.present
}

const shouldSaveToHistory = (
  savedState: IProjectData,
  currentState: IProjectData
): boolean => {
  return !isEqual(savedState, currentState)
}

// Middleware that captures state before action processing
const preActionMiddleware: Middleware = store => next => (action: unknown) => {
  if (isUndoableAction(action)) {
    const currentPresent = getCurrentProjectData(store)
    action.savedStateOfProjectData = currentPresent
  }

  if (isUpdatingUndoRedoMediaItems(action)) {
    // We should update past and feature data (undo/redo) when the uploading or generating assets already finished and ready
    const payload = (
      action as {
        payload: {
          id: string
          updates: TPartialMediaItem
          status: MediaStatus
        }[]
      }
    ).payload
    store.dispatch(
      projectDataSlice.actions.updateUndoRedoMediaItems(
        Array.isArray(payload) ? payload : [payload]
      )
    )
  }

  if (isUpdatingUndoRedoAudioItems(action)) {
    const payload = (
      action as {
        payload: {
          mediaPath: string
          mediaId: number
        }[]
      }
    ).payload

    store.dispatch(
      projectDataSlice.actions.updateUndoRedoAudioItems(
        Array.isArray(payload) ? payload : [payload]
      )
    )
  }

  next(action)
}

// Middleware that saves to history after action processing
const postActionMiddleware: Middleware = store => next => (action: unknown) => {
  const returnValue = next(action)

  const savedState = (action as IActionWithUndoConfig).savedStateOfProjectData

  if (savedState) {
    const currentPresent = getCurrentProjectData(store)

    if (shouldSaveToHistory(savedState, currentPresent)) {
      store.dispatch(projectDataSlice.actions.saveToPast(savedState))
    }
  }

  return returnValue
}

export default [preActionMiddleware, postActionMiddleware]
